import {
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryArgs,
    FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { clearEntraIdSession, getEntraIdAccessToken, refreshEntraIdSession } from "@/services/auth/entra-id.service";
import { userLoggedOut } from "@/store/slices/auth.slice";

export const prepareHeaders = async (headers: Headers) => {
    const token = await getEntraIdAccessToken();

    if (token) {
        headers.set("authorization", `Bearer ${token}`);
    }

    return headers;
};

const mutex = new Mutex();

const lockMutexAndRevalidate = async ({ dispatch }: Pick<BaseQueryApi, "dispatch">) => {
    const release = await mutex.acquire();
    try {
        await refreshEntraIdSession();
    } catch {
        await clearEntraIdSession();
        dispatch(userLoggedOut());
    } finally {
        release();
    }
};

/**
 * Extend fetchBaseQuery which automatically tries to acquire a new accessToken when 401 error is received.
 * with using async-mutex to prevent multiple acquiringTokens attempt when multiple calls fail with 401 Unauthorized errors.
 * source: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
 * @param args
 * @param api
 * @param extraOptions
 */
const createBaseQueryWithAuthRefresh = (baseQueryArgs: FetchBaseQueryArgs) => {
    const baseQuery = fetchBaseQuery(baseQueryArgs);

    const baseQueryWithAuthRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
        args,
        api,
        extraOptions,
    ) => {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();
        let result = await baseQuery(args, api, extraOptions);
        if (!result.error || result.error.status !== 401) {
            return result;
        }

        if (!mutex.isLocked()) {
            await lockMutexAndRevalidate(api);
        } else {
            await mutex.waitForUnlock();
        }

        result = await baseQuery(args, api, extraOptions);
        return result;
    };

    return baseQueryWithAuthRefresh;
};

export { createBaseQueryWithAuthRefresh };
